import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  GraduationCap,
  HeartPulse,
  Landmark,
  Server,
  Warehouse,
  Cable,
  Radio,
  Layers,
  Info,
  Loader2,
  FileText,
  HelpCircle,
  ChevronDown,
  ScrollText,
  Scale,
} from "lucide-react";
import { toast } from "sonner";

import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import api from "../lib/api";
import { COUNTRIES } from "../lib/countries";
import { useWizard } from "../state/wizard";

const SECTIONS = [
  { id: "sec-project", label: "Project Details" },
  { id: "sec-segment", label: "Segment" },
  { id: "sec-sub-segment", label: "Sub-Segment", conditional: true },
  { id: "sec-applications", label: "Applications" },
  { id: "sec-architecture", label: "Architecture" },
  { id: "sec-functional", label: "Functional Requirements" },
  { id: "sec-digital", label: "Digital Services" },
];

// Segments that offer a sub-segment picker.
const SEGMENTS_WITH_SUB = new Set(["Healthcare", "Education"]);

const DEFAULT_SEGMENT = "Default Specification";
const TIP_SESSION_KEY = "helvar-tooltip-coached";

const BASELINE_NOTE =
  "Please note: Occupancy Control, Daylight Control and Scheduling have been pre-selected. " +
  "Together these form the baseline for a compliant, safe and modern commercial building in line " +
  "with EPBD and Part L requirements. Untick them only if you are confident the project is exempt.";

const BASELINE_NOTE_EMERGENCY =
  "Emergency Monitoring is also pre-selected: BS 5266 requires monthly function tests, annual " +
  "duration tests and a written log for every emergency luminaire.";

const DIGITAL_BASELINE_NOTE =
  "Please note: Energy Monitoring and BMS/BAS Integration have been pre-selected. " +
  "Together these form the baseline for a compliant, safe and modern commercial building in line " +
  "with EPBD and Part L requirements. Untick them only if you are confident the project is exempt.";

const SEGMENT_META = {
  [DEFAULT_SEGMENT]: {
    icon: ScrollText,
    tooltip:
      "Use the Default Specification when the project is generic, unspecified or spans several building types. It assembles the cross-segment clause library so you still get a complete, compliant specification without committing to a single vertical.",
  },
  Healthcare: { icon: HeartPulse },
  Education: { icon: GraduationCap },
  Office: { icon: Building2 },
  Defence: { icon: Landmark },
  "Data Centre": { icon: Server },
  "Logistics & Distribution": { icon: Warehouse },
};

// Display order for the segment picker. Default Specification is offered first.
const SEGMENT_ORDER = [
  DEFAULT_SEGMENT,
  "Office",
  "Healthcare",
  "Education",
  "Data Centre",
  "Logistics & Distribution",
  "Defence",
];

const ARCH_META = {
  Wired: {
    icon: Cable,
    desc: "DALI - the open, IEC 62386 standard for lighting control. Two-core wiring, no polarity, mix any compliant manufacturer, and re-address without rewiring.",
  },
  Wireless: {
    icon: Radio,
    desc: "Bluetooth Mesh - the open, SIG-certified standard for wireless lighting. No control wiring, rapid deployment, mix any compliant node, and scale from a single room to a whole floor.",
  },
  Hybrid: {
    icon: Layers,
    desc: "The best of both. Mix wired and wireless in whatever combination suits the space, the people in it and the brief - full flexibility of choice.",
  },
};

export default function WizardPage() {
  const nav = useNavigate();
  const { inputs, setField, setFields, toggleArrayItem, setResult } = useWizard();
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [activeIdx, setActiveIdx] = useState(0);
  const [coached, setCoached] = useState(() => {
    try {
      return !!sessionStorage.getItem(TIP_SESSION_KEY);
    } catch {
      return true;
    }
  });

  const markCoached = () => {
    if (coached) return;
    try {
      sessionStorage.setItem(TIP_SESSION_KEY, "1");
    } catch {
      /* ignore */
    }
    setCoached(true);
  };

  const scrollRootRef = useRef(null);
  const sectionRefs = useRef({});

  // Which sections are visible for the current segment. Sub-Segment is only
  // shown for Healthcare / Education so the progress rail hides it otherwise.
  const visibleSections = useMemo(() => {
    return SECTIONS.filter(
      (s) => !s.conditional || SEGMENTS_WITH_SUB.has(inputs.segment)
    );
  }, [inputs.segment]);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/spec/schema")
      .then((r) => {
        if (!cancelled) setSchema(r.data);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  // Scroll → progress + active section
  useEffect(() => {
    const handler = () => {
      const root = scrollRootRef.current;
      if (!root) return;
      const rect = root.getBoundingClientRect();
      const total = root.offsetHeight - window.innerHeight;
      const scrolled = Math.max(0, -rect.top);
      const pct = total > 0 ? Math.min(1, scrolled / total) : 0;
      setProgress(pct);

      // Find current section (among visible only)
      let currentIdx = 0;
      visibleSections.forEach((s, i) => {
        const el = sectionRefs.current[s.id];
        if (!el) return;
        const r = el.getBoundingClientRect();
        if (r.top <= 160) currentIdx = i;
      });
      setActiveIdx(currentIdx);
    };
    handler();
    window.addEventListener("scroll", handler, { passive: true });
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("scroll", handler);
      window.removeEventListener("resize", handler);
    };
  }, [schema, visibleSections]);

  // Every application stays visible at all times (item: nothing is hidden).
  // The segment only drives which checkboxes are auto-ticked.
  const allApps = useMemo(() => {
    if (!schema) return [];
    return schema.applications.map((a) => ({
      ...a,
      typical: !!(inputs.segment && a.segments?.includes(inputs.segment)),
    }));
  }, [schema, inputs.segment]);

  // On a segment change, auto-tick the applications typical for that segment.
  // The Default Specification pre-ticks nothing — the user picks freely.
  const lastSegmentRef = useRef(inputs.segment);
  useEffect(() => {
    if (!schema) return;
    if (lastSegmentRef.current === inputs.segment) return;
    lastSegmentRef.current = inputs.segment;
    const auto =
      inputs.segment && inputs.segment !== DEFAULT_SEGMENT
        ? schema.applications
            .filter((a) => a.segments?.includes(inputs.segment))
            .map((a) => a.slug)
        : [];
    setFields({ applications: auto, sub_segment: "" });
    // Intentionally keyed on segment changes only: including `inputs` or
    // `setFields` here would re-run the auto-tick and wipe the user's manual
    // application edits on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputs.segment, schema]);

  // When a sub-segment is picked, auto-populate its recommended applications.
  const pickSubSegment = (slug) => {
    if (!schema) return;
    if (inputs.sub_segment === slug) {
      setFields({ sub_segment: "" });
      return;
    }
    const preset = schema.sub_segment_applications?.[slug] || [];
    setFields({ sub_segment: slug, applications: preset });
  };

  const architectureRecommendation = useMemo(() => {
    if (!schema || !inputs.segment) return null;
    const matches = schema.architecture_recommendations
      .filter((r) => r.segment === inputs.segment)
      .filter((r) => (inputs.applications || []).includes(r.application));
    if (!matches.length) return null;
    const appNameMap = Object.fromEntries(schema.applications.map((a) => [a.slug, a.name]));
    return matches.map((m) => ({
      rec: m.recommended,
      appName: appNameMap[m.application] || m.application,
    }));
  }, [schema, inputs.segment, inputs.applications]);

  const canGenerate = useMemo(() => {
    const subOK = SEGMENTS_WITH_SUB.has(inputs.segment) ? !!inputs.sub_segment : true;
    return (
      inputs.project_name.trim().length > 0 &&
      inputs.country.trim().length > 0 &&
      inputs.project_type &&
      inputs.segment &&
      subOK &&
      (inputs.applications || []).length > 0 &&
      inputs.architecture
    );
  }, [inputs]);

  const standardsLookup = useMemo(() => {
    const out = {};
    (schema?.standards || []).forEach((s) => {
      out[s.slug] = s;
    });
    return out;
  }, [schema]);

  const missingSummary = useMemo(() => {
    const m = [];
    if (!inputs.project_name.trim()) m.push("Project name");
    if (!inputs.country.trim()) m.push("Country");
    if (!inputs.project_type) m.push("Project type");
    if (!inputs.segment) m.push("Segment");
    if (SEGMENTS_WITH_SUB.has(inputs.segment) && !inputs.sub_segment) m.push("Sub-segment");
    if (!(inputs.applications || []).length) m.push("At least one application");
    if (!inputs.architecture) m.push("Architecture");
    return m;
  }, [inputs]);

  const generate = async () => {
    if (!canGenerate) return;
    setBusy(true);
    try {
      const r = await api.post("/spec/generate", { inputs });
      // Sync backend-enriched fields (e.g. sub_segment_name) back into the
      // wizard store so the Results page sidebar can render them.
      if (r.data?.inputs) {
        setFields({
          sub_segment_name: r.data.inputs.sub_segment_name || "",
        });
      }
      setResult(r.data);
      nav("/results");
    } catch (e) {
      toast.error("Could not generate specification", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setBusy(false);
    }
  };

  const jumpTo = (id) => {
    const el = sectionRefs.current[id];
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 120;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center text-[#4b5563]">
          Loading wizard…
        </div>
      </div>
    );
  }
  if (error || !schema) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <div className="text-[#c80f3f] font-medium">Failed to load wizard schema.</div>
          <div className="text-sm text-[#4b5563] mt-2">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={150}>
      <div data-testid="wizard-page" className="min-h-screen flex flex-col bg-white">
        <Header />

        {/* Sticky progress rail */}
        <div className="sticky top-16 z-30 bg-white/95 backdrop-blur-sm border-b border-[#e5e7eb]">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 py-3">
            <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.22em] font-semibold text-[#4b5563]">
              <span
                data-testid="progress-label"
                className="truncate max-w-[60%]"
              >
                {visibleSections[activeIdx]?.label || "Specification Builder"}
              </span>
              <span data-testid="progress-percent" className="font-mono">
                {Math.round(progress * 100)}%
              </span>
            </div>
            <div className="mt-2 h-1 w-full bg-[#e5e7eb] relative overflow-hidden">
              <div
                data-testid="progress-bar"
                className="absolute inset-y-0 left-0 bg-[#c80f3f] transition-[width] duration-200"
                style={{ width: `${progress * 100}%` }}
              />
            </div>
            <div className="mt-3 hidden md:flex items-center gap-1 flex-wrap">
              {visibleSections.map((s, i) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => jumpTo(s.id)}
                  data-testid={`jump-${i}`}
                  className={`text-[11px] px-2.5 py-1 rounded-sm border transition-colors ${
                    i === activeIdx
                      ? "border-[#1f2a44] bg-[#1f2a44] text-white"
                      : "border-[#e5e7eb] text-[#4b5563] hover:border-[#1f2a44] hover:text-[#1f2a44]"
                  }`}
                >
                  <span className="font-mono opacity-70 mr-1.5">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Wizard sections */}
        <div ref={scrollRootRef} className="max-w-4xl w-full mx-auto px-6 lg:px-8 pt-10 pb-4">
          <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#c80f3f]">
            Specification Builder
          </div>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-bold text-[#1f2a44] tracking-tight">
            Build your specification
          </h1>
          <p className="mt-3 text-sm text-[#4b5563] max-w-2xl leading-relaxed">
            Complete each section below. Your progress is saved automatically. When
            you are ready, generate the assembled specification.
          </p>

          {visibleSections.map((section, idx) => {
            const refFn = (el) => (sectionRefs.current[section.id] = el);
            const commonProps = {
              id: section.id,
              index: idx,
              title: section.label,
              innerRef: refFn,
              key: section.id,
            };
            switch (section.id) {
              case "sec-project":
                return (
                  <SectionBlock {...commonProps}>
                    <ProjectDetails schema={schema} inputs={inputs} setField={setField} />
                  </SectionBlock>
                );
              case "sec-segment":
                return (
                  <SectionBlock {...commonProps}>
                    <SegmentPicker
                      schema={schema}
                      inputs={inputs}
                      setField={setField}
                      coached={coached}
                      onCoached={markCoached}
                    />
                  </SectionBlock>
                );
              case "sec-sub-segment":
                return (
                  <SectionBlock {...commonProps}>
                    <SubSegmentPicker
                      subSegments={schema.sub_segments?.[inputs.segment] || []}
                      selectedSlug={inputs.sub_segment}
                      onPick={pickSubSegment}
                      segment={inputs.segment}
                    />
                  </SectionBlock>
                );
              case "sec-applications":
                return (
                  <SectionBlock {...commonProps}>
                    <ApplicationsPicker
                      apps={allApps}
                      selected={inputs.applications || []}
                      onToggle={(v) => toggleArrayItem("applications", v)}
                      onSetAll={(vals) => setField("applications", vals)}
                      segment={inputs.segment}
                      subSegmentActive={!!inputs.sub_segment}
                    />
                  </SectionBlock>
                );
              case "sec-architecture":
                return (
                  <SectionBlock {...commonProps}>
                    <ArchitecturePicker
                      inputs={inputs}
                      setField={setField}
                      recommendation={architectureRecommendation}
                    />
                  </SectionBlock>
                );
              case "sec-functional":
                return (
                  <SectionBlock {...commonProps}>
                    <FeatureGrid
                      items={schema.functional_requirements}
                      standardsLookup={standardsLookup}
                      selected={inputs.functional_requirements || []}
                      onToggle={(v) => toggleArrayItem("functional_requirements", v)}
                      onSetAll={(vals) => setField("functional_requirements", vals)}
                      testIdPrefix="feature"
                      baselineNote={BASELINE_NOTE}
                      baselineNoteSecondary={BASELINE_NOTE_EMERGENCY}
                      coached={coached}
                      onCoached={markCoached}
                      autoOpenFirst
                    />
                  </SectionBlock>
                );
              case "sec-digital":
                return (
                  <SectionBlock {...commonProps}>
                    <FeatureGrid
                      items={schema.digital_services}
                      standardsLookup={standardsLookup}
                      selected={inputs.digital_services || []}
                      onToggle={(v) => toggleArrayItem("digital_services", v)}
                      onSetAll={(vals) => setField("digital_services", vals)}
                      testIdPrefix="digital"
                      baselineNote={DIGITAL_BASELINE_NOTE}
                      coached={coached}
                      onCoached={markCoached}
                    />
                    <StandardsPanel
                      standards={schema.standards || []}
                      coached={coached}
                      onCoached={markCoached}
                    />
                  </SectionBlock>
                );
              default:
                return null;
            }
          })}

          {/* Generate action */}
          <div className="mt-12 border-t border-[#e5e7eb] pt-8">
            {missingSummary.length > 0 && (
              <div
                data-testid="missing-fields"
                className="mb-4 text-xs text-[#4b5563] border border-dashed border-[#c80f3f]/40 bg-[#c80f3f]/5 rounded-sm px-4 py-3"
              >
                Please complete: {missingSummary.join(" · ")}
              </div>
            )}
            <div className="flex flex-col sm:flex-row items-start sm:items-end justify-end gap-4">
              <Button
                onClick={generate}
                disabled={!canGenerate || busy}
                data-testid="generate-spec-button"
                className="bg-[#c80f3f] hover:bg-[#a10c32] text-white rounded-sm px-8 py-6 text-base disabled:opacity-40"
              >
                {busy ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.75} />
                    Assembling…
                  </>
                ) : (
                  <>
                    <FileText className="mr-2 h-4 w-4" strokeWidth={1.75} />
                    Generate Specification
                    <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.75} />
                  </>
                )}
              </Button>
            </div>
          </div>
          <div className="h-16" />
        </div>
      </div>
    </TooltipProvider>
  );
}

// ---------------------------------------------------------------------------
// Section wrapper
// ---------------------------------------------------------------------------

function SectionBlock({ id, index, title, children, innerRef }) {
  return (
    <section
      id={id}
      ref={innerRef}
      data-testid={`section-${index}`}
      className="mt-14 scroll-mt-40"
    >
      <div className="flex items-baseline gap-3">
        <span className="font-mono text-xs text-[#c80f3f]">
          {String(index + 1).padStart(2, "0")}
        </span>
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#1f2a44] tracking-tight">
          {title}
        </h2>
      </div>
      <Card className="mt-5 border border-[#e5e7eb] rounded-sm shadow-none">
        <div className="p-6 sm:p-8">{children}</div>
      </Card>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Section content
// ---------------------------------------------------------------------------

function ProjectDetails({ schema, inputs, setField }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
      <Field label="Project name" required>
        <Input
          value={inputs.project_name}
          onChange={(e) => setField("project_name", e.target.value)}
          placeholder="e.g. Riverside Hospital Refurbishment"
          data-testid="project-name-input"
          className="rounded-sm border-[#e5e7eb] focus-visible:ring-[#1f2a44]"
        />
      </Field>
      <Field label="Client name (optional)">
        <Input
          value={inputs.client_name}
          onChange={(e) => setField("client_name", e.target.value)}
          placeholder="e.g. NHS Trust"
          data-testid="client-name-input"
          className="rounded-sm border-[#e5e7eb] focus-visible:ring-[#1f2a44]"
        />
      </Field>
      <Field label="Country" required>
        <Select
          value={inputs.country}
          onValueChange={(v) => setField("country", v)}
        >
          <SelectTrigger
            data-testid="country-input"
            className="rounded-sm border-[#e5e7eb] focus:ring-[#1f2a44] bg-white"
          >
            <SelectValue placeholder="Select country" />
          </SelectTrigger>
          <SelectContent className="rounded-sm bg-white max-h-72">
            {COUNTRIES.map((c) => (
              <SelectItem
                key={c}
                value={c}
                data-testid={`country-option-${c.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`}
              >
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Project type" required>
        <Select
          value={inputs.project_type}
          onValueChange={(v) => setField("project_type", v)}
        >
          <SelectTrigger
            data-testid="project-type-select"
            className="rounded-sm border-[#e5e7eb] focus:ring-[#1f2a44] bg-white"
          >
            <SelectValue placeholder="Select project type" />
          </SelectTrigger>
          <SelectContent className="rounded-sm bg-white">
            {schema.project_types.map((pt) => (
              <SelectItem
                key={pt}
                value={pt}
                data-testid={`project-type-${pt.replace(/\s+/g, "-").toLowerCase()}`}
              >
                {pt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
    </div>
  );
}

function SegmentPicker({ schema, inputs, setField, coached, onCoached }) {
  const ordered = SEGMENT_ORDER.filter((s) => schema.segments.includes(s));
  return (
    <div>
      <p className="text-sm text-[#4b5563] leading-relaxed mb-6">
        Choose the specification you need. Pick a segment to tailor clause
        selection, priorities and product rules to that vertical — or start from
        the Default Specification for a generic or unspecified project.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {ordered.map((seg) => {
          const meta = SEGMENT_META[seg];
          const Icon = meta.icon;
          const selected = inputs.segment === seg;
          return (
            <div key={seg} className="relative">
              <button
                type="button"
                onClick={() => setField("segment", seg)}
                data-testid={`segment-option-${seg.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}
                className={`w-full text-left p-5 border rounded-sm transition-colors flex items-center gap-4 ${
                  selected
                    ? "border-[#1f2a44] bg-[#f4f5f7] ring-1 ring-[#1f2a44]"
                    : "border-[#e5e7eb] hover:border-[#1f2a44]"
                }`}
              >
                <Icon
                  className={`h-7 w-7 shrink-0 ${
                    selected ? "text-[#c80f3f]" : "text-[#1f2a44]"
                  }`}
                  strokeWidth={1.5}
                />
                <div className="font-display text-base font-semibold text-[#1f2a44] leading-tight pr-6">
                  {seg}
                </div>
              </button>
              {meta.tooltip && (
                <div className="absolute top-3 right-3">
                  <InfoTip
                    label={`About ${seg}`}
                    testId={`segment-info-${seg.toLowerCase().replace(/\s+/g, "-")}`}
                    coached={coached}
                    onCoached={onCoached}
                  >
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#c80f3f]">
                        When to use this
                      </div>
                      <div className="mt-1">{meta.tooltip}</div>
                    </div>
                  </InfoTip>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SubSegmentPicker({ subSegments, selectedSlug, onPick, segment }) {
  if (!segment) return null;
  return (
    <div>
      <p className="text-sm text-[#4b5563] leading-relaxed mb-6">
        Choose the sub-segment that best describes your project. Selecting a
        sub-segment will pre-tick the applications typically used in that
        setting — you can still adjust them in the next step.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {subSegments.map((ss) => {
          const selected = selectedSlug === ss.slug;
          return (
            <button
              key={ss.slug}
              type="button"
              onClick={() => onPick(ss.slug)}
              data-testid={`sub-segment-option-${ss.slug}`}
              className={`text-left p-5 border rounded-sm transition-colors ${
                selected
                  ? "border-[#1f2a44] bg-[#f4f5f7] ring-1 ring-[#1f2a44]"
                  : "border-[#e5e7eb] hover:border-[#1f2a44]"
              }`}
            >
              <div
                className={`font-display text-base font-semibold leading-tight ${
                  selected ? "text-[#c80f3f]" : "text-[#1f2a44]"
                }`}
              >
                {ss.name}
              </div>
              <p className="mt-2 text-xs text-[#4b5563] leading-relaxed">
                {ss.description}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ApplicationsPicker({ apps, selected, onToggle, onSetAll, segment, subSegmentActive }) {
  const allSlugs = apps.map((a) => a.slug);
  const allSelected = allSlugs.length > 0 && allSlugs.every((s) => selected.includes(s));
  const typicalSlugs = apps.filter((a) => a.typical).map((a) => a.slug);
  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <p className="text-sm text-[#4b5563] leading-relaxed max-w-xl">
          The full application list is always available. Selecting a segment
          pre-ticks the spaces typically found there — add or remove anything to
          match your project.
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          {typicalSlugs.length > 0 && (
            <button
              type="button"
              onClick={() => onSetAll(typicalSlugs)}
              data-testid="applications-reset-typical"
              className="text-[11px] uppercase tracking-[0.2em] font-semibold text-white bg-[#c80f3f] border border-[#c80f3f] px-3 py-1.5 rounded-sm hover:bg-[#a60c34] hover:border-[#a60c34] transition-colors"
            >
              Typical for {segment}
            </button>
          )}
          <button
            type="button"
            onClick={() => onSetAll(allSelected ? [] : allSlugs)}
            data-testid="applications-select-all"
            className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#1f2a44] border border-[#1f2a44] px-3 py-1.5 rounded-sm hover:bg-[#1f2a44] hover:text-white transition-colors"
          >
            {allSelected ? "Clear all" : "Select all"}
          </button>
          <div
            data-testid="applications-count"
            className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#4b5563] border border-[#d1d5db] bg-[#f4f5f7] px-3 py-1.5 rounded-sm"
          >
            {selected.length} selected
          </div>
        </div>
      </div>
      {subSegmentActive && (
        <div
          data-testid="applications-preselect-note"
          className="mb-5 flex items-start gap-3 border-l-2 border-[#1f2a44] bg-[#f4f5f7] rounded-sm px-4 py-3 text-sm text-[#1f2937] leading-relaxed"
        >
          <Info className="h-4 w-4 mt-0.5 text-[#1f2a44] shrink-0" strokeWidth={1.75} />
          <span>
            Applications typical for the selected sub-segment have been pre-ticked.
            Add or remove any to match your project.
          </span>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {apps.map((app) => {
          const isSel = selected.includes(app.slug);
          return (
            <button
              key={app.slug}
              type="button"
              onClick={() => onToggle(app.slug)}
              data-testid={`application-option-${app.slug}`}
              className={`text-left p-4 border rounded-sm transition-colors flex items-start gap-3 ${
                isSel
                  ? "border-[#1f2a44] bg-[#f4f5f7]"
                  : "border-[#e5e7eb] hover:border-[#1f2a44]"
              }`}
            >
              <TickBox on={isSel} />
              <div className="text-sm text-[#1f2937] font-medium leading-tight">
                <div>{app.name}</div>
                {app.typical && (
                  <div className="mt-1 text-[10px] uppercase tracking-[0.16em] font-semibold text-[#c80f3f]">
                    Typical
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function InfoTip({ children, label, testId, coached, onCoached, autoOpen = false, side = "top" }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!autoOpen || coached) return;
    const show = setTimeout(() => setOpen(true), 600);
    const hide = setTimeout(() => setOpen(false), 6500);
    return () => {
      clearTimeout(show);
      clearTimeout(hide);
    };
  }, [autoOpen, coached]);

  return (
    <Tooltip
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (v) onCoached?.();
      }}
    >
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          data-testid={testId}
          className={`shrink-0 h-6 w-6 flex items-center justify-center rounded-full border transition-colors ${
            coached
              ? "border-[#e5e7eb] text-[#4b5563] hover:text-[#c80f3f] hover:border-[#c80f3f]"
              : "border-[#c80f3f] text-[#c80f3f] tip-pulse"
          }`}
        >
          <HelpCircle className="h-3.5 w-3.5" strokeWidth={1.75} />
        </button>
      </TooltipTrigger>
      <TooltipContent
        side={side}
        className="max-w-[340px] bg-[#1f2a44] text-white text-xs leading-relaxed px-4 py-3 rounded-sm space-y-2"
      >
        {children}
      </TooltipContent>
    </Tooltip>
  );
}

function StandardsPanel({ standards, coached, onCoached }) {
  const [open, setOpen] = useState(false);
  if (!standards.length) return null;
  return (
    <div className="mt-8 border border-[#e5e7eb] rounded-sm">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="standards-panel-toggle"
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-[#f4f5f7] transition-colors"
      >
        <span className="flex items-center gap-3">
          <Scale className="h-4 w-4 text-[#c80f3f]" strokeWidth={1.75} />
          <span className="text-sm font-semibold text-[#1f2a44]">
            Standards &amp; compliance reference
          </span>
          <span className="hidden sm:inline text-xs text-[#4b5563]">
            EPBD · Part L · EN 12464-1 · BS 5266 · CSRD · WELL · LEED · BREEAM · NABERS UK
          </span>
        </span>
        <ChevronDown
          className={`h-4 w-4 text-[#4b5563] transition-transform ${open ? "rotate-180" : ""}`}
          strokeWidth={1.75}
        />
      </button>
      {open && (
        <div data-testid="standards-panel-body" className="px-4 pb-4 pt-1 border-t border-[#e5e7eb]">
          <p className="text-xs text-[#4b5563] leading-relaxed mt-3 mb-4">
            The legislation, certifications and lighting standards that shape a
            modern controls specification. Hover any reference for what it means
            and why lighting control matters to it.
          </p>
          <div className="flex flex-wrap gap-2">
            {standards.map((s) => (
              <div key={s.slug} className="flex items-center gap-1.5 border border-[#e5e7eb] rounded-sm pl-3 pr-1.5 py-1.5">
                <span className="text-xs font-semibold text-[#1f2a44]">{s.name}</span>
                <InfoTip
                  label={`About ${s.name}`}
                  testId={`standard-info-${s.slug}`}
                  coached={coached}
                  onCoached={onCoached}
                >
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#c80f3f]">
                      {s.name}
                    </div>
                    <div className="mt-0.5 font-semibold">{s.full_name}</div>
                    <div className="mt-1.5">{s.summary}</div>
                  </div>
                </InfoTip>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ArchitecturePicker({ inputs, setField, recommendation }) {
  return (
    <div className="space-y-6">
      <p className="text-sm text-[#4b5563] leading-relaxed">
        Choose the system architecture. This drives Part 2 functional clauses
        and Part 3 product clauses.
      </p>
      {recommendation && (
        <div
          data-testid="architecture-recommendation"
          className="border border-[#1f2a44] bg-[#f4f5f7] rounded-sm px-4 py-3 space-y-1.5"
        >
          {recommendation.map((r) => (
            <div key={`${r.appName}-${r.rec}`} className="flex items-start gap-3">
              <Info className="h-4 w-4 text-[#1f2a44] mt-0.5 shrink-0" strokeWidth={1.75} />
              <div className="text-sm text-[#1f2937]">
                <span className="font-semibold">Recommended:</span>{" "}
                <span className="text-[#c80f3f] font-semibold">{r.rec}</span> for {r.appName}.
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {Object.entries(ARCH_META).map(([arch, meta]) => {
          const Icon = meta.icon;
          const selected = inputs.architecture === arch;
          return (
            <button
              key={arch}
              type="button"
              onClick={() => setField("architecture", arch)}
              data-testid={`architecture-option-${arch.toLowerCase()}`}
              className={`text-left p-5 border rounded-sm transition-colors ${
                selected
                  ? "border-[#1f2a44] bg-[#f4f5f7] ring-1 ring-[#1f2a44]"
                  : "border-[#e5e7eb] hover:border-[#1f2a44]"
              }`}
            >
              <Icon
                className={`h-6 w-6 mb-4 ${
                  selected ? "text-[#c80f3f]" : "text-[#1f2a44]"
                }`}
                strokeWidth={1.5}
              />
              <div className="font-display text-base font-semibold text-[#1f2a44]">
                {arch}
              </div>
              <div className="mt-1 text-xs text-[#4b5563] leading-relaxed">
                {meta.desc}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function FeatureGrid({
  items,
  selected,
  onToggle,
  onSetAll,
  testIdPrefix,
  baselineNote,
  baselineNoteSecondary,
  standardsLookup = {},
  coached,
  onCoached,
  autoOpenFirst = false,
}) {
  const allSlugs = items.map((f) => f.slug);
  const allSelected = allSlugs.length > 0 && allSlugs.every((s) => selected.includes(s));
  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <p className="text-sm text-[#4b5563] leading-relaxed max-w-xl">
          Select the options to include.{" "}
          <span className={coached ? "" : "font-semibold text-[#c80f3f]"}>
            Hover any ⓘ for detail
          </span>{" "}
          — what it does, why it matters and which standards it satisfies.
        </p>
        <button
          type="button"
          onClick={() => onSetAll(allSelected ? [] : allSlugs)}
          data-testid={`${testIdPrefix}-select-all`}
          className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[#c80f3f] border border-[#c80f3f] px-3 py-1.5 rounded-sm hover:bg-[#c80f3f] hover:text-white transition-colors"
        >
          {allSelected ? "Clear all" : "Select all"}
        </button>
      </div>
      {baselineNote && (
        <div
          data-testid={`${testIdPrefix}-baseline-note`}
          className="mb-6 flex items-start gap-3 border-l-2 border-[#c80f3f] bg-[#f4f5f7] rounded-sm px-4 py-3 text-sm text-[#1f2937] leading-relaxed"
        >
          <Info className="h-4 w-4 mt-0.5 text-[#c80f3f] shrink-0" strokeWidth={1.75} />
          <div className="space-y-1.5">
            <div>{baselineNote}</div>
            {baselineNoteSecondary && (
              <div className="text-[#4b5563]">{baselineNoteSecondary}</div>
            )}
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {items.map((f, i) => (
          <FeatureCard
            key={f.slug}
            selected={selected.includes(f.slug)}
            onToggle={() => onToggle(f.slug)}
            item={f}
            standardsLookup={standardsLookup}
            coached={coached}
            onCoached={onCoached}
            autoOpen={autoOpenFirst && i === 0}
            testId={`${testIdPrefix}-${f.slug}`}
          />
        ))}
      </div>
    </div>
  );
}

function FeatureCard({
  selected,
  onToggle,
  item,
  testId,
  standardsLookup = {},
  coached,
  onCoached,
  autoOpen = false,
}) {
  const hasStory = item.what || item.why || item.client_benefit || item.compliance;
  const standards = (item.standards || []).map((s) => standardsLookup[s]).filter(Boolean);
  return (
    <div
      className={`border rounded-sm transition-colors ${
        selected ? "border-[#1f2a44] bg-[#f4f5f7]" : "border-[#e5e7eb] hover:border-[#1f2a44]"
      }`}
    >
      <div className="flex items-center gap-3 px-4 py-3">
        <button
          type="button"
          onClick={onToggle}
          data-testid={testId}
          className="flex items-center gap-3 flex-1 text-left"
        >
          <TickBox on={selected} />
          <div className="text-sm text-[#1f2937] font-medium leading-tight">
            <div>{item.name}</div>
            {standards.length > 0 && (
              <div className="mt-1 font-normal text-[11px] text-[#4b5563]">
                {standards.map((s) => s.name).join(" · ")}
              </div>
            )}
          </div>
        </button>
        {hasStory && (
          <InfoTip
            label={`About ${item.name}`}
            testId={`${testId}-info`}
            coached={coached}
            onCoached={onCoached}
            autoOpen={autoOpen}
          >
            {item.what && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#c80f3f]">
                  Feature Explained
                </div>
                <div className="mt-0.5">{item.what}</div>
              </div>
            )}
            {item.why && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#c80f3f]">
                  Why it matters
                </div>
                <div className="mt-0.5">{item.why}</div>
              </div>
            )}
            {item.client_benefit && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#c80f3f]">
                  Client benefit
                </div>
                <div className="mt-0.5">{item.client_benefit}</div>
              </div>
            )}
            {item.compliance && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#c80f3f]">
                  Standards &amp; legislation
                  {standards.length > 0 && ` — ${standards.map((s) => s.name).join(", ")}`}
                </div>
                <div className="mt-0.5">{item.compliance}</div>
              </div>
            )}
          </InfoTip>
        )}
      </div>
    </div>
  );
}

function TickBox({ on }) {
  return (
    <div
      className={`h-4 w-4 shrink-0 border rounded-[2px] flex items-center justify-center ${
        on ? "bg-[#c80f3f] border-[#c80f3f]" : "border-[#4b5563]"
      }`}
    >
      {on && (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="10"
          height="10"
          viewBox="0 0 24 24"
          fill="none"
          stroke="white"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      )}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#4b5563]">
        {label}
        {required && <span className="text-[#c80f3f] ml-1">*</span>}
      </Label>
      {children}
    </div>
  );
}
